import { ThinkingBlock } from "@/wizard/ThinkingBlock"
import { marked } from "marked"
import type { SplitWizOutcome, SplitWizPath, SplitWizPreviewLine } from "@/api/split_wizard"
import { exactCurrencyFormatter } from "@/utils"

const PATH_LABELS: Record<SplitWizPath, string> = {
  success: "Split applied",
  no_backup: "No backup",
  error: "Error",
}

marked.setOptions({ breaks: true, gfm: true })

export interface SplitWizPanelProps {
  outcome: SplitWizOutcome | null
  isLoading?: boolean
  loading_status_message?: string | null
  streaming_thinking?: string
  thinking_streaming?: boolean
  className?: string
  hasCachedOutcome?: boolean
}

export function SplitWizPanel({
  outcome,
  isLoading,
  loading_status_message,
  streaming_thinking,
  thinking_streaming,
  className,
  hasCachedOutcome,
}: SplitWizPanelProps) {
  if (isLoading) {
    return (
      <div class={`space-y-3 text-sm ${className ?? ""}`}>
        {loading_status_message ? (
          <p class="text-gray-700 flex items-start gap-2">
            <span
              class="mt-1.5 inline-block h-2 w-2 rounded-full bg-primary animate-pulse shrink-0"
              aria-hidden
            />
            <span>{loading_status_message}</span>
          </p>
        ) : (
          <p class="text-gray-500">Running CodingWiz…</p>
        )}
        {streaming_thinking && streaming_thinking.length > 0 ? (
          <ThinkingBlock
            content={streaming_thinking}
            isStreaming={!!thinking_streaming}
            defaultExpanded={true}
          />
        ) : null}
      </div>
    )
  }

  if (!outcome) {
    return null
  }

  const pathLabel = PATH_LABELS[outcome.path] ?? outcome.path
  const primary_bullet = outcome.summary_bullets[0]
  const detail_bullets = outcome.summary_bullets.slice(1)
  const summaryHtml = marked.parse(outcome.summary_markdown || "") as string

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      <div class="flex flex-wrap items-center gap-2">
        <span
          class={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${
            outcome.success ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"
          }`}>
          {pathLabel}
        </span>
        {!outcome.success && outcome.error ? (
          <span class="text-xs text-red-600 font-mono truncate max-w-full" title={outcome.error}>
            {outcome.error}
          </span>
        ) : null}
      </div>

      {hasCachedOutcome ? (
        <p class="text-xs text-gray-500">Showing previous split result.</p>
      ) : null}

      {primary_bullet ? <p class="text-sm text-gray-900">{primary_bullet}</p> : null}

      {outcome.success && outcome.proposed_split_preview.length > 0 ? (
        <div class="rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-2 text-sm">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-600">Split preview</p>
          <ul class="space-y-1 max-h-40 overflow-y-auto">
            {outcome.proposed_split_preview.map((line: SplitWizPreviewLine, i: number) => (
              <li key={i} class="text-slate-800 text-xs">
                <span class="font-medium">{exactCurrencyFormatter(line.amount)}</span>
                {line.customer_name ? <span class="text-slate-600"> · {line.customer_name}</span> : null}
                {line.retailer_name ? <span class="text-slate-600"> · {line.retailer_name}</span> : null}
                {line.product_line ? <span class="text-slate-600"> · {line.product_line}</span> : null}
                {line.expense_account ? (
                  <span class="text-slate-600" title={line.reason_code_id ?? undefined}>
                    {" "}
                    · {line.account_number ? `${line.account_number} ` : ""}
                    {line.expense_account}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {outcome.field_mapping_notes.length > 0 ? (
        <div class="rounded-md border border-slate-200 bg-white p-3 space-y-1">
          <p class="text-xs font-semibold text-slate-600">Field mapping</p>
          <ul class="list-disc pl-5 text-xs text-slate-700 space-y-0.5">
            {outcome.field_mapping_notes.map((n: string, i: number) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {(detail_bullets.length > 0 || outcome.summary_markdown || outcome.model_reasoning) && (
        <details class="border-t pt-2">
          <summary class="cursor-pointer text-sm font-medium text-gray-700">Show details & reasoning</summary>
          <div class="mt-3 space-y-3">
            {detail_bullets.length > 0 ? (
              <ul class="list-disc pl-5 space-y-1 text-sm text-gray-800">
                {detail_bullets.map((b: string, i: number) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            ) : null}
            {outcome.summary_markdown ? (
              <div class="prose prose-sm max-w-none border-t pt-3" dangerouslySetInnerHTML={{ __html: summaryHtml }} />
            ) : null}
            {outcome.model_reasoning ? (
              <div class="border-t pt-2">
                <p class="text-xs font-medium text-gray-500 mb-1">Model reasoning</p>
                <ThinkingBlock content={outcome.model_reasoning} isStreaming={false} defaultExpanded={false} />
              </div>
            ) : null}
          </div>
        </details>
      )}
    </div>
  )
}
